import React, { useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon, XCircleIcon } from 'lucide-react';
import { LedgerEntry, DocumentType, DocumentStatus, LedgerState } from './LedgerView';
import { formatAmount } from '../utils/currencyFormatter';
interface LedgerTableProps {
  entries: LedgerEntry[];
  onSelectEntry: (entry: LedgerEntry) => void;
  locale: string;
}
export function LedgerTable({
  entries,
  onSelectEntry,
  locale
}: LedgerTableProps) {
  const [sortField, setSortField] = useState<keyof LedgerEntry>('createdDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Handle sort
  const handleSort = (field: keyof LedgerEntry) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort entries
  const sortedEntries = [...entries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle null values
    if (aValue === null || aValue === undefined) aValue = 0;
    if (bValue === null || bValue === undefined) bValue = 0;
    // String comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    // Number comparison
    return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  // Get document type icon
  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case 'Invoice':
        return <ReceiptIcon className="h-4 w-4 text-purple-500" data-id="element-10277" />;
      case 'Purchase Order':
        return <ShoppingCartIcon className="h-4 w-4 text-blue-500" data-id="element-10278" />;
      case 'Credit Note':
        return <FileTextIcon className="h-4 w-4 text-orange-500" data-id="element-10279" />;
      case 'Expense Claim':
        return <CreditCardIcon className="h-4 w-4 text-green-500" data-id="element-10280" />;
      case 'Contract':
        return <FileTextIcon className="h-4 w-4 text-indigo-500" data-id="element-10281" />;
      case 'Payment':
        return <DollarSignIcon className="h-4 w-4 text-emerald-500" data-id="element-10282" />;
      default:
        return <FileTextIcon className="h-4 w-4 text-gray-500" data-id="element-10283" />;
    }
  };
  // Get document status badge
  const getDocumentStatusBadge = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700" data-id="element-10284">
            Draft
          </span>;
      case 'Approved':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10285">
            Approved
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10286">
            Exported
          </span>;
      case 'Paid':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800" data-id="element-10287">
            Paid
          </span>;
      case 'Cancelled':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-10288">
            Cancelled
          </span>;
      case 'Reversed':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" data-id="element-10289">
            Reversed
          </span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10290">
            {status}
          </span>;
    }
  };
  // Get ledger state indicator
  const getLedgerStateIndicator = (state: LedgerState) => {
    switch (state) {
      case 'Informational':
        return <InfoIcon className="h-4 w-4 text-blue-400" title="Informational - Draft entry" data-id="element-10291" />;
      case 'Active':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" title="Active" data-id="element-10292" />;
      case 'Reversed':
        return <XCircleIcon className="h-4 w-4 text-red-500" title="Reversed" data-id="element-10293" />;
      default:
        return null;
    }
  };
  // Column header with sort capability
  const SortableHeader = ({
    field,
    label
  }: {
    field: keyof LedgerEntry;
    label: string;
  }) => <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50" onClick={() => handleSort(field)} data-id="element-10294">
      <div className="flex items-center space-x-1" data-id="element-10295">
        <span data-id="element-10296">{label}</span>
        {sortField === field ? sortDirection === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-10297" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-10298" /> : null}
      </div>
    </th>;
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10299">
      <div className="overflow-x-auto" data-id="element-10300">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10301">
          <thead className="bg-gray-50 sticky top-0" data-id="element-10302">
            <tr data-id="element-10303">
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10304">
                State
              </th>
              <SortableHeader field="createdDate" label="Date" data-id="element-10305" />
              <SortableHeader field="documentNumber" label="Document #" data-id="element-10306" />
              <SortableHeader field="documentType" label="Type" data-id="element-10307" />
              <SortableHeader field="orgUnitName" label="Org Unit" data-id="element-10308" />
              <SortableHeader field="glCode" label="GL Code" data-id="element-10309" />
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10310">
                Debit (DR)
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10311">
                Credit (CR)
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10312">
                Balance
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10313">
            {sortedEntries.length === 0 ? <tr data-id="element-10314">
                <td colSpan={9} className="px-6 py-8 text-center text-gray-500" data-id="element-10315">
                  <AlertCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-10316" />
                  <p data-id="element-10317">No ledger entries found matching your criteria.</p>
                </td>
              </tr> : sortedEntries.map(entry => {
            const isInformational = entry.ledgerState === 'Informational';
            const isReversed = entry.ledgerState === 'Reversed';
            const isReversal = entry.ledgerEntryType === 'Reversal';
            return <tr key={entry.ledgerEntryId} className={`hover:bg-gray-50 cursor-pointer transition-colors ${isInformational ? 'bg-blue-50 bg-opacity-30' : ''} ${isReversed ? 'bg-red-50 bg-opacity-20' : ''}`} onClick={() => onSelectEntry(entry)} data-id="element-10318">
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-10319">
                      {getLedgerStateIndicator(entry.ledgerState)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10320">
                      <div data-id="element-10321">
                        {new Date(entry.createdDate).toLocaleDateString(locale, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-10322">
                        {new Date(entry.createdDate).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-10323">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10324">
                        {entry.documentNumber}
                      </div>
                      {isReversal && entry.reversesLedgerEntryId && <div className="text-xs text-orange-600 flex items-center" data-id="element-10325">
                          <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-10326" />
                          Reverses entry
                        </div>}
                      <div className="text-xs text-gray-500" data-id="element-10327">
                        {getDocumentStatusBadge(entry.documentStatus)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-10328">
                      <div className="flex items-center" data-id="element-10329">
                        {getDocumentTypeIcon(entry.documentType)}
                        <span className="ml-2" data-id="element-10330">{entry.documentType}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-10331">
                      <div className="max-w-xs truncate" title={entry.orgUnitPath} data-id="element-10332">
                        {entry.orgUnitName}
                      </div>
                      {(entry.vendorName || entry.userName) && <div className="text-xs text-gray-500 truncate" data-id="element-10333">
                          {entry.vendorName || entry.userName}
                        </div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono" data-id="element-10334">
                      {entry.glCode}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono ${isInformational ? 'text-gray-400' : 'text-green-700 font-semibold'}`} data-id="element-10335">
                      {entry.debitAmount ? formatAmount(entry.debitAmount, locale) : ''}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono ${isInformational ? 'text-gray-400' : 'text-red-700 font-semibold'}`} data-id="element-10336">
                      {entry.creditAmount ? formatAmount(entry.creditAmount, locale) : ''}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono font-bold ${isInformational ? 'text-gray-400' : 'text-indigo-700'}`} data-id="element-10337">
                      {entry.calculatedRunningBalance !== undefined ? formatAmount(entry.calculatedRunningBalance, locale) : ''}
                    </td>
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200" data-id="element-10338">
        <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-10339">
          <div className="flex items-center space-x-4" data-id="element-10340">
            <div className="flex items-center" data-id="element-10341">
              <InfoIcon className="h-4 w-4 text-blue-400 mr-1" data-id="element-10342" />
              <span data-id="element-10343">Informational (Draft)</span>
            </div>
            <div className="flex items-center" data-id="element-10344">
              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-10345" />
              <span data-id="element-10346">Active</span>
            </div>
            <div className="flex items-center" data-id="element-10347">
              <XCircleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-10348" />
              <span data-id="element-10349">Reversed</span>
            </div>
          </div>
          <div className="text-gray-500" data-id="element-10350">
            {sortedEntries.length}{' '}
            {sortedEntries.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>
      </div>
    </div>;
}